import { FC, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { AiChatMessageType } from "@/graphql/__generated__/graphql";
import { TFunction } from "i18next";
import { StreamPhase, ChatStatus } from "@/constants/enums";
import { cn } from "@/lib/utils";
import MessageRenderer from "./MessageRenderer";
import AssistantMessageWrapper from "./AssistantMessageWrapper";
import { StreamingMarkdownText } from "./StreamingMarkdownText";
import SourcesList from "@/pages/EditTender/DocumentChat/Components/SourceCard";
import { normalizeSources } from "@/pages/EditTender/DocumentChat/utils";
import ErrorCard from "./ErrorCard";
import ProcessingStagesCard from "./ProcessingStagesCard";
import ToolCallList from "./ToolCallList";
import CircleLoader from "../../shared/loaders/CircleLoader";
import { formatErrorMessage } from "../error-handling";
import type { UseChatStreamingReturn } from "@/hooks/chat/useChatStreaming";

/**
 * Props for the PendingQuestionSection component
 */
interface PendingQuestionSectionProps {
  /** The current pending question */
  pendingQuestion: string | null;
  /** The aiImportId for chat context */
  aiImportId: number | undefined;
  /** Streaming state object containing all streaming-related data */
  streamingState: UseChatStreamingReturn;
  /** i18n translate function */
  t: TFunction;
  /** Handler for refreshing/resending messages */
  onRefresh: () => void;
  /** Handler for resending messages */
  onResend: (_resendText: string) => void;
  /** Function to scroll an element to top of container */
  scrollElementToTop: (_element: HTMLElement) => void;
  /** Whether pending question should be pushed up */
  pushUpPendingQuestion: boolean;
  /** Scroll container height for padding calculation */
  scrollContainerHeight: number;
}

/**
 * Component that renders the pending question and streaming response section.
 * Handles all UI related to the current question being processed.
 *
 * @component
 * @example
 * <PendingQuestionSection
 *   pendingQuestion="What is the timeline?"
 *   aiImportId={123}
 *   streamingState={streamingState}
 *   t={t}
 *   onRefresh={handleRefresh}
 *   onResend={handleResend}
 *   {...otherProps}
 * />
 */
export const PendingQuestionSection: FC<PendingQuestionSectionProps> = ({
  pendingQuestion,
  aiImportId,
  streamingState,
  t,
  onRefresh,
  onResend,
  scrollElementToTop,
  pushUpPendingQuestion,
  scrollContainerHeight: _scrollContainerHeight,
}) => {
  const pendingQuestionRef = useRef<HTMLDivElement | null>(null);

  // Compute isStreaming from streamStatus
  const isStreaming = useMemo(
    () =>
      streamingState.streamStatus === ChatStatus.CONNECTING ||
      streamingState.streamStatus === ChatStatus.OPEN,
    [streamingState.streamStatus]
  );

  // Destructure streaming state for easier access
  const {
    phase,
    streamingContent,
    thinkingText,
    toolCalls,
    currentNode,
    processingStagesComplete,
    error,
    sourcesFromMetadata,
    thinkingDurationMs,
  } = streamingState;

  /**
   * Scroll pending question to top only when it first appears.
   * Separate effect to prevent continuous scrolling during streaming.
   * Uses a small delay to ensure the DOM has been updated with the new question.
   */
  useEffect(() => {
    if (pendingQuestion && pushUpPendingQuestion && pendingQuestionRef.current) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        scrollElementToTop(pendingQuestionRef.current!);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [pendingQuestion, pushUpPendingQuestion, scrollElementToTop]);

  if (!pendingQuestion) {
    return null;
  }

  return (
    <>
      {/* Show pending question (the question being asked right now) */}
      <div ref={pendingQuestionRef}>
        <MessageRenderer
          key="pending-question"
          message={{
            id: -1,
            userId: 0,
            aiImportId: (aiImportId as number) ?? 0,
            text: pendingQuestion,
            type: AiChatMessageType.Question,
          }}
          t={t}
          onResend={onResend}
        />
      </div>

      {/* Error card - shown when streaming fails or AI service is unavailable */}
      {error && !isStreaming && (
        <ErrorCard
          error={error}
          t={t}
          onRetry={() => onResend(pendingQuestion)}
          isRetrying={false}
        />
      )}

      {/* Processing stages card (shown during thinking phase and early answering phase) */}
      {isStreaming &&
        (phase === StreamPhase.THINKING ||
          (phase === StreamPhase.ANSWERING && streamingContent?.length === 0)) &&
        !error && (
        <ProcessingStagesCard
          currentNode={currentNode}
          processingStagesComplete={processingStagesComplete}
          thinkingText={thinkingText}
          thinkingDurationMs={thinkingDurationMs}
          t={t}
        />
      )}

      {/* Tool call statuses during streaming for pending question */}
      {isStreaming && toolCalls.length > 0 && <ToolCallList toolCalls={toolCalls} />}

      {/* Loader spinner before answer is streamed - only for pending question */}
      {isStreaming &&
        phase === StreamPhase.ANSWERING &&
        streamingContent?.length === 0 &&
        !error && (
        <motion.div
          className="flex items-center gap-2 mx-4"
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <CircleLoader height={20} width={20} strokeWidth={2} />
          <p className="text-sm text-muted-foreground">
            {t("documentchat.streamedresponse.answering")}
          </p>
        </motion.div>
      )}

      {/* Render the live streaming assistant response - only show when there's a pending question */}
      {(streamingContent?.length ?? 0) > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="min-w-0 max-w-full"
        >
          <AssistantMessageWrapper t={t} copyText={streamingContent} onRefresh={onRefresh}>
            <StreamingMarkdownText content={streamingContent} />
            {Array.isArray(sourcesFromMetadata) && sourcesFromMetadata.length > 0 && (
              <div className="mt-3">
                <SourcesList sources={normalizeSources(sourcesFromMetadata)} qaSetId={"live"} />
              </div>
            )}
            {error && (
              <div
                className={cn(
                  "border-destructive bg-destructive/10 dark:bg-destructive/5 text-destructive mt-2 rounded-md border p-3 text-sm dark:text-red-200"
                )}
              >
                {formatErrorMessage(error)}
              </div>
            )}
          </AssistantMessageWrapper>
        </motion.div>
      )}
    </>
  );
};
